import type { MarketplaceBriefSnapshot } from "@nexus/contracts";
import { useTranslation } from "react-i18next";

export interface OrderPersonaBriefSectionProps {
  snapshot: MarketplaceBriefSnapshot;
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  const trimmed = value?.trim();
  if (!trimmed) return null;
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="whitespace-pre-wrap text-sm text-foreground">{trimmed}</dd>
    </div>
  );
}

export function OrderPersonaBriefSection({ snapshot }: OrderPersonaBriefSectionProps) {
  const { t } = useTranslation();
  const selectedHook =
    snapshot.hooks.find((hook) => hook.hookIndex === snapshot.selectedHookIndex) ??
    snapshot.hooks[0];
  const creator = [
    snapshot.creatorProfile.ageRange,
    snapshot.creatorProfile.gender,
    snapshot.creatorProfile.preferences,
  ]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(" · ");

  return (
    <section className="flex flex-col gap-3 rounded-md border border-border bg-muted/20 p-3">
      <h3 className="text-sm font-medium text-foreground">
        {t("marketplace.orders.list.panel.personaBriefHeading", "Brief details")}
      </h3>
      <dl className="flex flex-col gap-3">
        <Field
          label={t("marketplace.orders.list.panel.personaBriefSelectedHook", "Selected hook")}
          value={
            selectedHook
              ? [selectedHook.direction, selectedHook.line].filter((part) => part.trim()).join(" — ")
              : null
          }
        />
        <Field
          label={t("marketplace.orders.list.panel.personaBriefConcept", "Concept")}
          value={[snapshot.concept.direction, snapshot.concept.line]
            .filter((part) => part.trim())
            .join(" — ")}
        />
        <Field
          label={t("marketplace.orders.list.panel.personaBriefCta", "CTA")}
          value={[snapshot.cta.direction, snapshot.cta.line]
            .filter((part) => part.trim())
            .join(" — ")}
        />
        <Field
          label={t("marketplace.orders.list.panel.personaBriefCreator", "Creator profile")}
          value={creator}
        />
        <Field
          label={t("marketplace.orders.list.panel.personaBriefBrandProduct", "Product link")}
          value={snapshot.brand.productLink}
        />
        <Field
          label={t("marketplace.orders.list.panel.personaBriefBrandFont", "Font")}
          value={snapshot.brand.font}
        />
        <Field
          label={t("marketplace.orders.list.panel.personaBriefBrandLogo", "Logo")}
          value={snapshot.brand.logo}
        />
        <Field
          label={t("marketplace.orders.list.panel.personaBriefBrandDisclaimer", "Disclaimer")}
          value={snapshot.brand.disclaimer}
        />
        <Field
          label={t("marketplace.orders.list.panel.personaBriefNotes", "Notes")}
          value={snapshot.notes}
        />
        {snapshot.hooks.length > 1 ? (
          <div className="flex flex-col gap-1.5">
            <dt className="text-xs font-medium text-muted-foreground">
              {t("marketplace.orders.list.panel.personaBriefAllHooks", "All hooks")}
            </dt>
            <dd className="flex flex-col gap-1.5">
              {snapshot.hooks.map((hook) => (
                <p key={hook.hookIndex} className="text-sm text-foreground">
                  <span className="font-medium">
                    {t("marketplace.orders.list.panel.personaBriefHookIndex", "Hook {{index}}", {
                      index: hook.hookIndex,
                    })}
                    {hook.hookIndex === snapshot.selectedHookIndex
                      ? ` (${t("marketplace.orders.list.panel.personaBriefHookSelected", "selected")})`
                      : ""}
                    :
                  </span>{" "}
                  {[hook.direction, hook.line].filter((part) => part.trim()).join(" — ")}
                </p>
              ))}
            </dd>
          </div>
        ) : null}
      </dl>
    </section>
  );
}
